import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  ArrowUpRight,
  Microscope,
  RefreshCw,
  Search,
  Sparkles,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import TopBar from "../components/TopBar";
import { apiClient } from "../lib/api";
import { fmtNum, fmtInr, fmtPct, pnlClass } from "../lib/format";

const verdictStyle = {
  STRONG_BUY: "bg-profit text-white border-transparent",
  BUY: "bg-[hsl(var(--profit)/0.12)] text-profit border-[hsl(var(--profit)/0.4)]",
  HOLD: "bg-saffron-soft text-saffron border-[hsl(var(--saffron)/0.4)]",
  REDUCE: "bg-[hsl(var(--warning)/0.15)] text-[hsl(var(--warning))] border-[hsl(var(--warning)/0.4)]",
  AVOID: "bg-[hsl(var(--loss)/0.12)] text-loss border-[hsl(var(--loss)/0.4)]",
};

function VerdictBadge({ verdict, conviction }) {
  if (!verdict) {
    return (
      <span className="px-2 py-0.5 border border-border text-muted-foreground text-[9px] uppercase tracking-[0.14em] rounded font-mono">
        Pending
      </span>
    );
  }
  return (
    <span
      className={`px-2 py-0.5 border text-[9px] font-bold uppercase tracking-[0.14em] rounded font-mono whitespace-nowrap ${
        verdictStyle[verdict] || "bg-card border-border"
      }`}
    >
      {verdict.replace("_", " ")}
      {conviction ? ` · ${conviction[0]}` : ""}
    </span>
  );
}

function fmtRatio(v) {
  if (v == null || isNaN(v)) return "—";
  return Number(v).toFixed(1);
}

function fmtPctRatio(v) {
  if (v == null || isNaN(v)) return "—";
  return `${(Number(v) * 100).toFixed(1)}%`;
}

const COLS = [
  { key: "symbol", label: "Instrument", align: "left", get: (r) => (r.symbol || "").toLowerCase() },
  { key: "quantity", label: "Qty", align: "right", get: (r) => Number(r.quantity) || 0 },
  { key: "avg_cost", label: "Avg. cost", align: "right", get: (r) => Number(r.avg_cost) || 0 },
  { key: "ltp", label: "LTP", align: "right", get: (r) => (r.ltp != null ? Number(r.ltp) : -Infinity) },
  { key: "day_change_pct", label: "Day chg.", align: "right", get: (r) => (r.day_change_pct != null ? Number(r.day_change_pct) : -Infinity) },
  { key: "invested", label: "Exposure", align: "right", get: (r) => Number(r.invested) || 0 },
  { key: "unrealized_pnl", label: "P&L", align: "right", get: (r) => (r.unrealized_pnl != null ? Number(r.unrealized_pnl) : -Infinity) },
  { key: "change_pct", label: "Net chg.", align: "right", get: (r) => (r.change_pct != null ? Number(r.change_pct) : -Infinity) },
  { key: "num_clients", label: "Clients", align: "right", get: (r) => Number(r.num_clients) || 0 },
  { key: "pe", label: "P/E", align: "right", get: (r) => (r.ratios?.trailing_pe != null ? Number(r.ratios.trailing_pe) : -Infinity) },
  { key: "roe", label: "ROE", align: "right", get: (r) => (r.ratios?.return_on_equity != null ? Number(r.ratios.return_on_equity) : -Infinity) },
  { key: "verdict", label: "AI Verdict", align: "left", get: (r) => r.verdict || "zz" },
];

function SortIcon({ active, dir }) {
  if (!active) return <ArrowUpDown className="h-3 w-3 opacity-40" strokeWidth={2} />;
  return dir === "asc"
    ? <ArrowUp className="h-3 w-3 text-lime" strokeWidth={2.5} />
    : <ArrowDown className="h-3 w-3 text-lime" strokeWidth={2.5} />;
}

export default function AdvisorResearch() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState("invested");
  const [sortDir, setSortDir] = useState("desc");
  const [analyzeState, setAnalyzeState] = useState(null);
  const pollRef = useRef(null);
  const autoStartedRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/advisor/research/holdings");
      setRows(data.items || []);
      return data.items || [];
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load research holdings");
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const startPolling = useCallback(() => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await apiClient.get("/advisor/research/analyze-status");
        setAnalyzeState(data);
        if (!data.running) {
          stopPolling();
          await load();
          if ((data.total || 0) > 0) toast.success("AI analysis complete for all holdings");
        }
      } catch {
        stopPolling();
      }
    }, 4000);
  }, [load]);

  const triggerAnalyze = useCallback(async (force = false) => {
    try {
      const { data } = await apiClient.post(`/advisor/research/analyze-all${force ? "?force=true" : ""}`);
      setAnalyzeState(data);
      if (data.running || data.started) startPolling();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start analysis");
    }
  }, [startPolling]);

  useEffect(() => {
    (async () => {
      const items = await load();
      // Check if a batch run is already in progress (e.g. daily pre-warm)
      try {
        const { data } = await apiClient.get("/advisor/research/analyze-status");
        setAnalyzeState(data);
        if (data.running) {
          startPolling();
          return;
        }
      } catch { /* ignore */ }
      // Auto-analyze anything missing a fresh verdict (user opted for auto mode)
      if (!autoStartedRef.current && items.some((r) => !r.verdict)) {
        autoStartedRef.current = true;
        triggerAnalyze(false);
      }
    })();
    return stopPolling;
  }, [load, startPolling, triggerAnalyze]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let arr = rows;
    if (q) {
      arr = rows.filter(
        (r) =>
          (r.symbol || "").toLowerCase().includes(q) ||
          (r.name || "").toLowerCase().includes(q) ||
          (r.sector || "").toLowerCase().includes(q)
      );
    }
    const col = COLS.find((c) => c.key === sortKey);
    if (!col) return arr;
    const sorted = [...arr].sort((a, b) => {
      const va = col.get(a);
      const vb = col.get(b);
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [rows, query, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir(key === "symbol" || key === "verdict" ? "asc" : "desc");
    }
  };

  const totalExposure = rows.reduce((s, r) => s + (r.invested || 0), 0);
  const totalValue = rows.reduce((s, r) => s + (r.market_value || 0), 0);
  const analyzed = rows.filter((r) => r.verdict).length;
  const running = analyzeState?.running;

  return (
    <div className="min-h-screen bg-background">
      <TopBar subtitle="Research Desk" />
      <main className="px-4 sm:px-6 lg:px-10 py-5 sm:py-8 max-w-[1600px] mx-auto">
        <button
          onClick={() => nav("/advisor")}
          data-testid="research-back"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-lime mb-5 font-mono uppercase tracking-[0.2em] transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} />
          Dashboard
        </button>

        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6">
          <div>
            <div className="text-[10px] text-muted-foreground tracking-[0.28em] uppercase font-mono font-semibold flex items-center gap-1.5">
              <Microscope className="h-3 w-3" strokeWidth={1.5} />
              Across the entire book
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold mt-1 tracking-tighter">
              Research <span className="text-lime">desk</span>.
            </h1>
          </div>
          <button
            data-testid="analyze-all-btn"
            onClick={() => triggerAnalyze(false)}
            disabled={running}
            className="self-start border border-border hover:border-lime hover:text-lime px-4 py-2.5 text-xs font-bold uppercase tracking-[0.18em] rounded-md flex items-center gap-2 disabled:opacity-60 font-mono"
          >
            {running ? (
              <RefreshCw className="h-3.5 w-3.5 animate-spin" strokeWidth={2} />
            ) : (
              <Sparkles className="h-3.5 w-3.5" strokeWidth={2} />
            )}
            {running
              ? `Analyzing ${analyzeState.done}/${analyzeState.total}${analyzeState.current ? ` · ${analyzeState.current}` : ""}`
              : "Analyze all with AI"}
          </button>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <div className="card-noir p-4 rounded-lg" data-testid="research-stat-instruments">
            <div className="text-[10px] tracking-[0.24em] uppercase font-mono text-muted-foreground font-semibold">Instruments</div>
            <div className="font-display text-2xl font-extrabold mt-1.5 tabular">{rows.length}</div>
          </div>
          <div className="card-noir p-4 rounded-lg">
            <div className="text-[10px] tracking-[0.24em] uppercase font-mono text-muted-foreground font-semibold">Book exposure</div>
            <div className="font-display text-2xl font-extrabold mt-1.5 tabular">{fmtInr(totalExposure)}</div>
          </div>
          <div className="card-noir p-4 rounded-lg">
            <div className="text-[10px] tracking-[0.24em] uppercase font-mono text-muted-foreground font-semibold">Current value</div>
            <div className={`font-display text-2xl font-extrabold mt-1.5 tabular ${pnlClass(totalValue - totalExposure)}`}>
              {fmtInr(totalValue)}
            </div>
          </div>
          <div className="card-noir p-4 rounded-lg" data-testid="research-stat-analyzed">
            <div className="text-[10px] tracking-[0.24em] uppercase font-mono text-muted-foreground font-semibold">AI analyzed</div>
            <div className="font-display text-2xl font-extrabold mt-1.5 tabular">
              {analyzed}<span className="text-muted-foreground text-base">/{rows.length}</span>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-4 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
          <input
            data-testid="research-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search symbol, company or sector…"
            className="w-full bg-card border border-border rounded-md pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring font-mono"
          />
        </div>

        {loading ? (
          <div className="card-noir p-12 text-center text-sm text-muted-foreground rounded-lg">Loading book holdings…</div>
        ) : rows.length === 0 ? (
          <div className="card-noir p-16 text-center rounded-lg">
            <Microscope className="h-10 w-10 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
            <div className="font-display text-xl font-bold mb-1">No open holdings across the book</div>
            <div className="text-sm text-muted-foreground">Record BUY transactions for clients to start researching.</div>
          </div>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="space-y-2 sm:hidden" data-testid="research-cards">
              {filtered.map((r) => (
                <Link
                  key={r.symbol}
                  to={`/stocks/${encodeURIComponent(r.symbol)}`}
                  className="card-noir block p-4 rounded-lg"
                  data-testid={`research-card-${r.symbol}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-mono text-sm font-bold">{r.symbol}</div>
                    <VerdictBadge verdict={r.verdict} conviction={r.conviction} />
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate mb-3">
                    {r.name}{r.sector ? ` · ${r.sector}` : ""}
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-[11px]">
                    {[
                      ["LTP", r.ltp != null ? fmtNum(r.ltp) : "—"],
                      ["Day %", r.day_change_pct != null ? fmtPct(r.day_change_pct) : "—", pnlClass(r.day_change_pct)],
                      ["Net %", r.change_pct != null ? fmtPct(r.change_pct) : "—", pnlClass(r.change_pct)],
                      ["Exposure", fmtInr(r.invested)],
                      ["P&L", r.unrealized_pnl != null ? fmtInr(r.unrealized_pnl) : "—", pnlClass(r.unrealized_pnl)],
                      ["Clients", r.num_clients],
                    ].map(([k, v, cls], i) => (
                      <div key={`${k}-${i}`}>
                        <div className="text-muted-foreground uppercase tracking-[0.18em] text-[9px] font-mono">{k}</div>
                        <div className={`font-mono tabular text-foreground ${cls || ""}`}>{v}</div>
                      </div>
                    ))}
                  </div>
                </Link>
              ))}
            </div>

            {/* Desktop table */}
            <div className="hidden sm:block card-noir overflow-x-auto rounded-lg">
              <table className="w-full text-[13px]" data-testid="research-table">
                <thead className="border-b border-border bg-secondary/40">
                  <tr className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-mono">
                    {COLS.map((c) => {
                      const active = sortKey === c.key;
                      return (
                        <th
                          key={c.key}
                          data-testid={`research-sort-${c.key}`}
                          onClick={() => toggleSort(c.key)}
                          className={`p-3 font-semibold select-none cursor-pointer hover:text-foreground ${
                            c.align === "right" ? "text-right" : "text-left"
                          }`}
                        >
                          <span className={`inline-flex items-center gap-1.5 ${c.align === "right" ? "justify-end" : ""}`}>
                            {c.label}
                            <SortIcon active={active} dir={sortDir} />
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r, idx) => (
                    <tr
                      key={r.symbol}
                      data-testid={`research-row-${r.symbol}`}
                      onClick={() => nav(`/stocks/${encodeURIComponent(r.symbol)}`)}
                      className={`border-b border-border/70 last:border-b-0 hover:bg-secondary/60 transition-colors cursor-pointer ${
                        idx % 2 === 1 ? "bg-secondary/20" : ""
                      }`}
                    >
                      <td className="p-3">
                        <div className="inline-flex flex-col min-w-0 group">
                          <div className="inline-flex items-center gap-1 font-mono text-[13px] font-semibold text-foreground group-hover:text-lime transition-colors">
                            {r.symbol}
                            <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={2} />
                          </div>
                          <div className="text-[11px] text-muted-foreground truncate max-w-[220px]">
                            {r.name}{r.sector ? ` · ${r.sector}` : ""}
                          </div>
                        </div>
                      </td>
                      <td className="p-3 text-right font-mono tabular">{fmtNum(r.quantity, 2)}</td>
                      <td className="p-3 text-right font-mono tabular">{fmtNum(r.avg_cost)}</td>
                      <td className="p-3 text-right font-mono tabular">{r.ltp != null ? fmtNum(r.ltp) : "—"}</td>
                      <td className={`p-3 text-right font-mono tabular font-semibold ${pnlClass(r.day_change_pct)}`}>
                        {r.day_change_pct != null ? fmtPct(r.day_change_pct) : "—"}
                      </td>
                      <td className="p-3 text-right font-mono tabular">{fmtInr(r.invested)}</td>
                      <td className={`p-3 text-right font-mono tabular font-semibold ${pnlClass(r.unrealized_pnl)}`}>
                        {r.unrealized_pnl != null ? fmtInr(r.unrealized_pnl) : "—"}
                      </td>
                      <td className={`p-3 text-right font-mono tabular font-semibold ${pnlClass(r.change_pct)}`}>
                        {r.change_pct != null ? fmtPct(r.change_pct) : "—"}
                      </td>
                      <td className="p-3 text-right font-mono tabular">
                        <span
                          className="inline-flex items-center gap-1 justify-end"
                          title={(r.clients || []).map((c) => `${c.name} (${fmtNum(c.quantity, 0)})`).join(", ")}
                        >
                          <Users className="h-3 w-3 text-muted-foreground" strokeWidth={2} />
                          {r.num_clients}
                        </span>
                      </td>
                      <td className="p-3 text-right font-mono tabular">{fmtRatio(r.ratios?.trailing_pe)}</td>
                      <td className="p-3 text-right font-mono tabular">{fmtPctRatio(r.ratios?.return_on_equity)}</td>
                      <td className="p-3">
                        <VerdictBadge verdict={r.verdict} conviction={r.conviction} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-3 text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground">
              Click any instrument for full fundamentals, financials, news & AI checklist
            </div>
          </>
        )}
      </main>
    </div>
  );
}
